<script lang="ts">
import { defineComponent } from "vue"
import { LMap, LTileLayer, LMarker } from "vue2-leaflet"
import { Icon } from "leaflet"

type D = Icon.Default & {
    _getIconUrl?: string
}

delete (Icon.Default.prototype as D)._getIconUrl
Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

export default defineComponent({
    name: "LocationSelector",
    props: {
        value: { type: Object, required: true },
    },
    components: {
        LMap,
        LTileLayer,
        LMarker,
    },
    data() {
        return {
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            zoom: 2,
            center: [46, 21],
            precision: 5,
            data: {
                latitude: null as null | string,
                longitude: null as null | string,
            },
            savedPosition: {
                latitude: null as null | string,
                longitude: null as null | string,
            },
            validationErrors: {
                latitude: null as null | string,
                longitude: null as null | string,
            },
            inputTimeout: null as null | number,
            isManualInput: false,
        }
    },
    computed: {
        markerPosition() {
            const lat = this.data.latitude != null ? parseFloat(this.data.latitude) : NaN
            const lng = this.data.longitude != null ? parseFloat(this.data.longitude) : NaN

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return null
            }

            return [lat, lng]
        },
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler: function (coordinates) {
                if (this.isManualInput) {
                    return
                }

                if (!coordinates) {
                    return
                }

                const { latitude, longitude } = coordinates
                const lat = parseFloat(latitude)
                const lng = parseFloat(longitude)

                if (
                    latitude == null ||
                    longitude == null ||
                    !Number.isFinite(lat) ||
                    !Number.isFinite(lng)
                ) {
                    this.data = {
                        latitude: null,
                        longitude: null,
                    }
                    this.savedPosition = {
                        latitude: null,
                        longitude: null,
                    }

                    return
                }

                const newLatitude = lat.toFixed(6)
                const newLongitude = lng.toFixed(6)

                if (this.data.latitude !== newLatitude || this.data.longitude !== newLongitude) {
                    this.data = {
                        latitude: newLatitude,
                        longitude: newLongitude,
                    }

                    this.savedPosition = { ...this.data }

                    this.center = [lat, lng]
                }
            },
        },
    },
    methods: {
        restoreCoordinates() {
            if (this.savedPosition.latitude !== null && this.savedPosition.longitude !== null) {
                this.data = {
                    latitude: this.savedPosition.latitude,
                    longitude: this.savedPosition.longitude,
                }
                this.center = [
                    parseFloat(this.savedPosition.latitude),
                    parseFloat(this.savedPosition.longitude),
                ]
                this.$emit("input", this.data)
            }
        },
        getCurrentLocation() {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    this.successLocation,
                    (error) => {
                        console.error("Error getting location:", error)
                    },
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
                )
            }
        },
        successLocation(position: GeolocationPosition) {
            const { coords } = position

            this.isManualInput = false
            this.data = {
                latitude: coords.latitude.toFixed(6),
                longitude: coords.longitude.toFixed(6),
            }
            this.validationErrors = {
                latitude: null,
                longitude: null,
            }

            this.$emit("input", this.data)
            //@ts-ignore
            this.$refs.map?.mapObject.setZoom(16, { animate: true })
            //@ts-ignore
            this.$refs.map?.mapObject.panTo([coords.latitude, coords.longitude], {
                animate: true,
            })
        },
        handleMapClick(event: any) {
            const { latlng } = event
            this.isManualInput = false
            this.data = {
                latitude: latlng.lat.toFixed(6),
                longitude: latlng.lng.toFixed(6),
            }
            this.validationErrors = {
                latitude: null,
                longitude: null,
            }
            this.$emit("input", this.data)
        },
        clearCoordinates() {
            this.isManualInput = false
            this.data = {
                latitude: null,
                longitude: null,
            }
            this.validationErrors = {
                latitude: null,
                longitude: null,
            }
            this.$emit("input", this.data)
        },
        validateLatitude(value: string): string | null {
            if (!value) return null
            const num = parseFloat(value)
            if (isNaN(num)) return this.$t("location.validation.invalid_latitude_format")
            if (num < -90 || num > 90) return this.$t("location.validation.latitude_range")
            return null
        },
        validateLongitude(value: string): string | null {
            if (!value) return null
            const num = parseFloat(value)
            if (isNaN(num)) return this.$t("location.validation.invalid_longitude_format")
            if (num < -180 || num > 180) return this.$t("location.validation.longitude_range")
            return null
        },
        handleLatitudeInput(event: Event) {
            const target = event.target as HTMLInputElement
            const value = target.value

            this.isManualInput = true
            this.data.latitude = value
            this.validationErrors.latitude = this.validateLatitude(value)

            this.debouncedUpdateCoordinates()
        },
        handleLongitudeInput(event: Event) {
            const target = event.target as HTMLInputElement
            const value = target.value

            this.isManualInput = true
            this.data.longitude = value
            this.validationErrors.longitude = this.validateLongitude(value)

            this.debouncedUpdateCoordinates()
        },
        debouncedUpdateCoordinates() {
            if (this.inputTimeout) {
                clearTimeout(this.inputTimeout)
            }

            this.inputTimeout = window.setTimeout(() => {
                this.updateCoordinatesFromInput()
            }, 500)
        },
        updateCoordinatesFromInput() {
            const lat = this.data.latitude !== null ? parseFloat(this.data.latitude) : NaN
            const lng = this.data.longitude !== null ? parseFloat(this.data.longitude) : NaN
            const latValid = Number.isFinite(lat)
            const lngValid = Number.isFinite(lng)

            if (
                !this.validationErrors.latitude &&
                !this.validationErrors.longitude &&
                latValid &&
                lngValid
            ) {
                this.$emit("input", {
                    latitude: lat,
                    longitude: lng,
                })

                this.center = [lat, lng]
                this.zoom = Math.max(this.zoom, 10)

                setTimeout(() => {
                    //@ts-ignore
                    this.$refs.map?.mapObject.setView([lat, lng], this.zoom, { animate: true })
                }, 100)
            } else if (this.data.latitude !== null || this.data.longitude !== null) {
                this.$emit("input", {
                    latitude: latValid ? lat : null,
                    longitude: lngValid ? lng : null,
                })
            }

            setTimeout(() => {
                this.isManualInput = false
            }, 100)
        },
    },
    beforeUnmount() {
        if (this.inputTimeout) {
            clearTimeout(this.inputTimeout)
        }
    },
})
</script>

<template>
    <div class="selector">
        <div class="row">
            <div class="col-md-6">
                <div class="form-group">
                    <label for="latitude">{{ $t("location.latitude") }}</label>
                    <input
                        id="latitude"
                        type="number"
                        min="-90"
                        max="90"
                        step="0.000001"
                        :value="data.latitude"
                        @input="handleLatitudeInput"
                        class="form-control"
                        :class="{ 'is-invalid': validationErrors.latitude }"
                        :placeholder="$t('location.enter_latitude')"
                    />
                    <div v-if="validationErrors.latitude" class="invalid-feedback">
                        {{ validationErrors.latitude }}
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label for="longitude">{{ $t("location.longitude") }}</label>
                    <input
                        id="longitude"
                        type="number"
                        min="-180"
                        max="180"
                        step="0.000001"
                        :value="data.longitude"
                        @input="handleLongitudeInput"
                        class="form-control"
                        :class="{ 'is-invalid': validationErrors.longitude }"
                        :placeholder="$t('location.enter_longitude')"
                    />
                    <div v-if="validationErrors.longitude" class="invalid-feedback">
                        {{ validationErrors.longitude }}
                    </div>
                </div>
            </div>
            <div class="col-md-12 d-flex justify-content-end gap-2">
                <button @click="getCurrentLocation" type="button" class="btn btn-outline-primary">
                    {{ $t("location.use_current_location") }}
                </button>
                <button
                    @click="clearCoordinates"
                    :disabled="data.latitude == null || data.longitude == null"
                    type="button"
                    class="btn btn-outline-danger"
                >
                    {{ $t("location.clear_coordinates") }}
                </button>
            </div>
        </div>
        <div class="map">
            <l-map
                ref="map"
                style="height: 400px; z-index: 0"
                :zoom="zoom"
                :center="center"
                :options="{
                    attributionControl: false,
                    doubleClickZoom: false,
                    minZoom: 2,
                    maxZoom: 18,
                }"
                @click="handleMapClick"
            >
                <l-tile-layer :url="url" :attribution="null"></l-tile-layer>
                <l-marker v-if="markerPosition !== null" :lat-lng="markerPosition"></l-marker>
            </l-map>
        </div>
    </div>
</template>

<style scoped>
.map {
    width: 100%;
    height: 400px;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 6px 6px 16px #00000070;
}

.form-control.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: #dc3545;
}

.gap-2 > *:not(:last-child) {
    margin-right: 0.5rem;
}
</style>
